import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Heart, CheckCircle2, Linkedin } from "lucide-react";
import PageHero from "../components/PageHero";
import { TEAM, WHY_CHOOSE, COMPANY } from "../mock/mock";

const About = () => {
  return (
    <div>
      <PageHero
        title="À propos"
        subtitle="Notre expertise au service de votre gouvernance et de votre agilité."
        image="https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      {/* Intro */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="w-14 h-[3px] bg-[#2f80c4] mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-[#15324f] mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Qui sommes-nous ?
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Agile Connexion INC est un cabinet de conseil spécialisé en gouvernance des technologies de l'information (TI) et en transformation agile. Nous accompagnons les organisations dans l'optimisation de leurs structures de gestion et l'alignement stratégique de leurs TI.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              Nous aidons les directions et les équipes à adopter des cadres de travail agiles afin d'accroître leur efficacité opérationnelle, leur réactivité et la valeur livrée à leurs parties prenantes.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Notre approche combine rigueur, pragmatisme et transfert de savoir, pour des résultats concrets et durables.
            </p>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Équipe Agile Connexion"
              className="rounded-lg shadow-xl w-full h-[420px] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#15324f] text-white px-8 py-6 rounded-lg shadow-lg">
              <p className="text-3xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>20+</p>
              <p className="text-sm text-white/75">années d'expertise combinée</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Valeurs */}
      <section className="bg-[#f4f6f9] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Target, title: "Notre mission", text: "Accompagner les organisations vers une gouvernance TI performante et une agilité durable." },
            { icon: Eye, title: "Notre vision", text: "Être le partenaire de référence des dirigeants qui souhaitent aligner technologie, stratégie et performance." },
            { icon: Heart, title: "Nos valeurs", text: "Rigueur, transparence, collaboration et engagement envers la réussite de nos clients." }
          ].map((b) => (
            <div key={b.title} className="bg-white p-9 rounded-lg shadow-sm border-t-4 border-[#2f80c4]">
              <b.icon size={36} className="text-[#2f80c4] mb-5" />
              <h3 className="text-xl font-bold text-[#15324f] mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>{b.title}</h3>
              <p className="text-slate-600 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Distinction */}
      <section id="distinction" className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-[#15324f] uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Notre distinction
            </h2>
            <div className="w-16 h-[3px] bg-[#2f80c4] mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 max-w-4xl mx-auto">
            {WHY_CHOOSE.points.map((p) => (
              <div key={p.title} className="flex items-start gap-4">
                <CheckCircle2 size={24} className="text-[#2f80c4] mt-1 shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#15324f] text-lg mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>{p.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="equipe" className="bg-[#f4f6f9] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-[#15324f] uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Notre équipe d'experts-conseils
            </h2>
            <div className="w-16 h-[3px] bg-[#2f80c4] mt-5" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {TEAM.map((m) => (
              <div
                key={m.id}
                className={`rounded-lg p-7 text-center transition-shadow duration-300 ${
                  m.placeholder
                    ? "bg-white/60 border-2 border-dashed border-slate-200"
                    : "bg-white shadow-sm hover:shadow-lg"
                }`}
              >
                <div
                  className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center text-2xl font-bold mb-5 ${
                    m.placeholder
                      ? "bg-slate-100 text-slate-300"
                      : "bg-gradient-to-br from-[#1c3e5e] to-[#2f80c4] text-white"
                  }`}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {m.initials}
                </div>
                <h4 className={`font-bold text-lg ${m.placeholder ? "text-slate-400" : "text-[#15324f]"}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {m.name}
                </h4>
                {m.role && (
                  <div className="flex items-center justify-center gap-1.5 mt-1 mb-3">
                    <p className="text-[#2f80c4] text-sm font-medium">{m.role}</p>
                    {m.linkedin && (
                      <a
                        href={m.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`LinkedIn de ${m.name}`}
                        className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded bg-[#2f80c4]/10 text-[#2f80c4] hover:bg-[#2f80c4] hover:text-white transition-colors"
                      >
                        <Linkedin size={12} />
                      </a>
                    )}
                  </div>
                )}
                <p className="text-slate-500 text-sm leading-relaxed">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#15324f] py-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Travaillons ensemble
          </h2>
          <p className="text-white/75 mb-8">Contactez-nous au {COMPANY.phone} ou par courriel.</p>
          <Link to="/nous-joindre" className="inline-flex items-center gap-2 bg-[#2f80c4] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wide rounded hover:bg-white hover:text-[#15324f] transition-colors duration-300" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Nous joindre <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
